import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { ApiResponse, PaginatedApiResponse } from '@card-software/nexus-utilities';
import { LabDipRequest } from '../../models/lab-dip/lab-dip-request';
import { LabDipRequestSearch } from '../../models/commands/lab-dip-request-search';
import { ExistingColorMatch } from '../../models/lab-dip/existing-color-match';

@Injectable({
  providedIn: 'root',
})
export class LabDipRequestProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.itemApi + '/api/v1/lab-dip-request';

  public getLabDipRequests(): Observable<ApiResponse<LabDipRequest[]>> {
    return this.httpClient.get<ApiResponse<LabDipRequest[]>>(`${this.baseUrl}/all`);
  }

  public getLabDipRequest(id: string): Observable<ApiResponse<LabDipRequest>> {
    return this.httpClient.get<ApiResponse<LabDipRequest>>(`${this.baseUrl}/${id}`);
  }

  public getLabDipRequestByLabDipId(id: string): Observable<ApiResponse<LabDipRequest>> {
    return this.httpClient.get<ApiResponse<LabDipRequest>>(`${this.baseUrl}/lab-dip/${id}`);
  }

  public search(
    searchCommand: LabDipRequestSearch,
    pageNumber = 1,
    pageSize = 25,
    sortField?: string,
    sortOrder?: number,
  ): Observable<ApiResponse<PaginatedApiResponse<LabDipRequest>>> {
    let url = `${this.baseUrl}/search?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (sortField) {
      url += `&sortField=${sortField}&sortOrder=${sortOrder ?? -1}`;
    }
    return this.httpClient.post<ApiResponse<PaginatedApiResponse<LabDipRequest>>>(url, searchCommand);
  }

  public upsertLabDipRequest(labDipRequest: LabDipRequest): Observable<ApiResponse<LabDipRequest>> {
    return this.httpClient.post<ApiResponse<LabDipRequest>>(`${this.baseUrl}`, labDipRequest);
  }

  public assignLabDipIdToColor(labDipId: string, labDipRequestId: string, colorId: string): Observable<ApiResponse<LabDipRequest>> {
    const params = new HttpParams().set('labDipId', labDipId).set('labDipRequestId', labDipRequestId).set('colorId', colorId);

    return this.httpClient.put<ApiResponse<LabDipRequest>>(`${this.baseUrl}/assign`, null, {
      params,
    });
  }

  public unassignLabDipIdFromColor(labDipId: string, labDipRequestId: string, colorId: string): Observable<ApiResponse<LabDipRequest>> {
    const params = new HttpParams().set('labDipId', labDipId).set('labDipRequestId', labDipRequestId).set('colorId', colorId);

    return this.httpClient.delete<ApiResponse<LabDipRequest>>(`${this.baseUrl}/unassign`, {
      params,
    });
  }

  public getColorNamesByClient(customerReference: string): Observable<ApiResponse<string[]>> {
    return this.httpClient.get<ApiResponse<string[]>>(`${this.baseUrl}/${customerReference}/color-names`);
  }

  /** Other requests of the customer that already have this color on any of the fabric codes (warning only, ADO #5876). */
  public findExistingColorMatches(
    customerReference: string,
    colorName: string,
    fabricCodes: string[],
    excludeRequestId?: string,
  ): Observable<ApiResponse<ExistingColorMatch[]>> {
    let params = new HttpParams().set('colorName', colorName);
    fabricCodes.forEach((code) => (params = params.append('fabricCodes', code)));
    if (excludeRequestId) {
      params = params.set('excludeRequestId', excludeRequestId);
    }
    return this.httpClient.get<ApiResponse<ExistingColorMatch[]>>(`${this.baseUrl}/${customerReference}/color-matches`, { params });
  }

  public getByColorId(colorId: string): Observable<ApiResponse<LabDipRequest>> {
    return this.httpClient.get<ApiResponse<LabDipRequest>>(`${this.baseUrl}/by-color/${colorId}`);
  }
}
